import type { TranslationFunction } from "@src/packages/i18n";
import {
  FETBetaOrderLookupPendingError,
  FETBetaOrderUnavailableError,
} from "@src/packages/cycles/useFETBetaLifecycle";
import {
  CycleOutcomeWriteError,
  isClinicAuthoredOutcome,
} from "@src/packages/cycles/presenters/patientOutcomeWrite";

/**
 * What to tell the patient when recording a beta result fails.
 *
 * The modal stays open and editable on failure, so the message is the only thing
 * that says whether trying again will help. Two rejections are recoverable and
 * were previously indistinguishable from a server outage: an earlier attempt
 * already stored a record (tapping save again now updates it), and a record the
 * clinic authored is not the patient's to overwrite (nothing here will change
 * it). Everything else stays deliberately generic — API detail is not patient
 * copy.
 *
 * Shared by the two screens that record a beta so the same failure cannot say
 * two different things depending on where the patient started.
 */
export function betaResultErrorMessage(
  error: unknown,
  t: TranslationFunction,
  /**
   * Who is looking. A supporter is refused for a different reason than the
   * patient ever is, and the refusal alone cannot tell them apart.
   */
  viewer?: { isSupporting?: boolean; cycleOwnerName?: string },
  /**
   * The record a correction was aimed at. Its provenance is the only thing that
   * says whether a refusal is genuinely about a clinic's record.
   */
  existingRecord?: { sourceKind?: string | null } | null,
): string {
  /**
   * A supporter first, whatever went wrong.
   *
   * They cannot author an outcome on any cycle — `assertOutcomeSourceAuthority`
   * accepts a patient or a provider and refuses everyone else, on both the create
   * and the correction endpoint — so no failure they meet is one a retry can
   * clear, and which failure it was does not change what to tell them.
   *
   * Checked before the FET branches for a reason. The FET lifecycle query is
   * disabled for a partner cycle, so its lookup never resolves and a supporter on
   * an FET cycle was answered "we're still loading beta tracking, try again in a
   * moment" — permanently, and about the wrong thing.
   */
  if (viewer?.isSupporting) {
    return viewer.cycleOwnerName
      ? t("care.beta_result.only_they_can_record_this_result", {
          params: { name: viewer.cycleOwnerName },
        })
      : t("care.beta_result.only_the_person_this_cycle_belongs_to_can_record");
  }
  /**
   * Not a transient failure, so it must not read like one.
   *
   * This fires when the cycle has no transfer recorded, and a beta result has
   * nothing to attach to without one. The old copy said to refresh and try
   * again — advice that could never come true, because refreshing does not
   * create the transfer. Naming the step that does is the only honest answer.
   */
  if (error instanceof FETBetaOrderUnavailableError) {
    return t("care.beta_result.confirm_your_transfer_before_recording_a_beta");
  }
  if (error instanceof FETBetaOrderLookupPendingError) {
    return t("care.beta_result.fet_order_lookup_pending");
  }
  if (error instanceof CycleOutcomeWriteError) {
    if (error.isConflict) return t("care.beta_result.earlier_entry_saved");
    /**
     * A refused correction means the record is not the patient's to change — but
     * only a clinic-authored one may be described that way. An ending saved
     * through "Add past cycle" carries no provenance at all, because the
     * cycle-intake schema omits it, and the correction endpoint refuses that too.
     * Calling it a clinic's record told a patient their clinic had entered
     * something they had entered themselves minutes earlier.
     *
     * Tracked for the backend as louise-life/dashboard-api#1470: until a patient's
     * own entry records them as its author, this stays unfixable in the app and
     * the honest message is that it cannot be changed here.
     */
    if (error.isForeignRecord) {
      return isClinicAuthoredOutcome(existingRecord?.sourceKind)
        ? t("care.beta_result.recorded_by_your_clinic")
        : t("care.beta_result.result_cannot_be_changed_here");
    }
  }
  return t("care.cycle_detail.alerts.error_generic");
}
